import { ReactElement, ReactNode } from 'react';
import Divider from '@mui/material/Divider';
import Fade from '@mui/material/Fade';
import Stack from '@mui/material/Stack';
import { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import mixinSx from '@watershed/style/mixinSx';

/**
 * Container for settings pages with sidebar.
 *
 * @example
 * ```tsx
 * <Page.Root>
 *   <Page.Header>
 *     <Page.TitleContainer>
 *       <Page.Title>Your Title Here</Page.Title>
 *       <Page.Subtitle>Your Subtitle Here</Page.Subtitle>
 *     </Page.TitleContainer>
 *     <Page.Actions>
 *       <Button variant="contained">Action</Button>
 *     </Page.Actions>
 *   </Page.Header>
 * </Page.Root>
 * ```
 */
function PageRoot({
  children,
  outerSx,
  innerSx,
  component = 'div',
  fadeIn = true,
}: {
  children: ReactNode;
  outerSx?: SxProps<Theme>;
  innerSx?: SxProps<Theme>;
  component?: React.ElementType;
  fadeIn?: boolean;
}) {
  return (
    <Stack
      component={component}
      data-scroll-container
      sx={mixinSx(
        {
          position: 'relative',
          p: 3,
          width: '100%',
          height: '100%',
          overflowY: 'scroll',
          outline: (theme) => `0.5px solid ${theme.palette.grey30}`,
          backgroundColor: (theme) => theme.palette.white,
          zIndex: 3,
        },
        outerSx
      )}
    >
      <Fade in={fadeIn} timeout={300}>
        <Stack
          gap={3}
          sx={mixinSx(
            {
              position: 'relative',
              maxWidth: '1280px',
              width: '100%',
              height: '100%',
              margin: '0 auto',
            },
            innerSx
          )}
        >
          {children}
        </Stack>
      </Fade>
    </Stack>
  );
}

/**
 * Generic wrapper component for the header section of a settings page.
 * Handles layout and spacing between title, subtitle, and actions.
 */
function PageHeader({
  children,
  sx,
}: {
  children: ReactNode;
  sx?: SxProps<Theme>;
}) {
  return (
    <Stack gap={3}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        sx={sx}
      >
        {children}
      </Stack>
      <Divider />
    </Stack>
  );
}

/**
 * Component for rendering the main title text of a settings page.
 */
function PageTitle({ children }: { children: ReactNode | ReactElement }) {
  return <Typography variant="h3">{children}</Typography>;
}

/**
 * Component for rendering the subtitle text of a settings page.
 */
function PageSubtitle({ children }: { children: ReactNode | ReactElement }) {
  return (
    <Typography
      variant="body2"
      sx={{
        color: (theme) => theme.palette.text.secondary,
        textWrap: 'balance',
      }}
    >
      {children}
    </Typography>
  );
}

function PageTitleContainer({ children }: { children: ReactNode }) {
  return <Stack>{children}</Stack>;
}

/**
 * Component for rendering action buttons or elements in the settings page header.
 */
function PageActions({ children }: { children: ReactNode }) {
  return (
    <Stack direction="row" gap={1} flexShrink={0}>
      {children}
    </Stack>
  );
}

const Page = {
  Root: PageRoot,
  Header: PageHeader,
  Title: PageTitle,
  Subtitle: PageSubtitle,
  TitleContainer: PageTitleContainer,
  Actions: PageActions,
};

export default Page;
